import React from 'react';

/**
 * Lightweight, dependency-free Markdown renderer for Zyni chat responses.
 *
 * Zyni Testing Cycle 1, Follow-up 2 (User Acceptance): the assistant bubble was rendering
 * `entry.content` as a raw string, so Markdown emphasis like "**organization**" showed the
 * literal asterisks instead of bold text. Scoped to exactly what Zyni's responses actually
 * use (bold, italic, inline code, bullet/numbered lists, paragraphs) rather than pulling in
 * a full Markdown library — no new dependency, renders through React elements (never
 * dangerouslySetInnerHTML), so it stays exactly as safe against injected HTML/script content
 * as the plain-text rendering it replaces.
 */

const INLINE_PATTERN = /(\*\*[^*]+\*\*|`[^`]+`|\*[^*]+\*)/g;

function renderInline(text: string, keyPrefix: string): React.ReactNode[] {
  return text
    .split(INLINE_PATTERN)
    .filter((part) => part !== '')
    .map((part, index) => {
      const key = `${keyPrefix}-${index}`;

      if (part.length >= 4 && part.startsWith('**') && part.endsWith('**')) {
        return <strong key={key}>{part.slice(2, -2)}</strong>;
      }

      if (part.length >= 2 && part.startsWith('`') && part.endsWith('`')) {
        return <code key={key}>{part.slice(1, -1)}</code>;
      }

      if (part.length >= 2 && part.startsWith('*') && part.endsWith('*')) {
        return <em key={key}>{part.slice(1, -1)}</em>;
      }

      return part;
    });
}

const BULLET_LINE = /^\s*[-*]\s+/;
const NUMBERED_LINE = /^\s*\d+\.\s+/;

type LineRunKind = 'bullet' | 'numbered' | 'prose';

function classifyLine(line: string): LineRunKind {
  if (BULLET_LINE.test(line)) {
    return 'bullet';
  }

  if (NUMBERED_LINE.test(line)) {
    return 'numbered';
  }

  return 'prose';
}

/**
 * Zyni sometimes leads a list with an intro line and no blank line before the bullets
 * ("It helps with:\n- purpose\n- expectations"), which is not itself a separate paragraph
 * block. Rather than requiring an entire block to be uniformly one kind, split it into
 * consecutive same-kind runs (prose / bullet / numbered) and render each run as its own
 * paragraph or list — this still renders a clean, uniform block exactly as before, and
 * additionally renders the mixed "intro sentence, then list" shape correctly instead of
 * falling back to literal dashes in a paragraph.
 */
function renderBlock(block: string, blockKey: string): React.ReactNode {
  const lines = block
    .split('\n')
    .map((line) => line.trimEnd())
    .filter((line) => line.trim() !== '');

  if (lines.length === 0) {
    return null;
  }

  const runs: { kind: LineRunKind; lines: string[] }[] = [];
  lines.forEach((line) => {
    const kind = classifyLine(line);
    const lastRun = runs[runs.length - 1];
    if (lastRun && lastRun.kind === kind) {
      lastRun.lines.push(line);
    } else {
      runs.push({ kind, lines: [line] });
    }
  });

  return (
    <React.Fragment key={blockKey}>
      {runs.map((run, runIndex) => {
        const runKey = `${blockKey}-r${runIndex}`;

        if (run.kind === 'bullet') {
          return (
            <ul key={runKey} className="zyni-markdown-list">
              {run.lines.map((line, index) => (
                <li key={`${runKey}-${index}`}>{renderInline(line.replace(BULLET_LINE, ''), `${runKey}-${index}`)}</li>
              ))}
            </ul>
          );
        }

        if (run.kind === 'numbered') {
          return (
            <ol key={runKey} className="zyni-markdown-list">
              {run.lines.map((line, index) => (
                <li key={`${runKey}-${index}`}>{renderInline(line.replace(NUMBERED_LINE, ''), `${runKey}-${index}`)}</li>
              ))}
            </ol>
          );
        }

        return (
          <p key={runKey} className="zyni-markdown-paragraph">
            {run.lines.map((line, index) => (
              <React.Fragment key={`${runKey}-${index}`}>
                {index > 0 && <br />}
                {renderInline(line, `${runKey}-${index}`)}
              </React.Fragment>
            ))}
          </p>
        );
      })}
    </React.Fragment>
  );
}

export function renderZyniMarkdown(text: string): React.ReactNode {
  if (!text) {
    return text;
  }

  return text
    .split(/\n{2,}/)
    .filter((block) => block.trim() !== '')
    .map((block, index) => renderBlock(block, `zm-${index}`));
}

export default function ZyniMarkdown({ text }: { text: string }): JSX.Element {
  return <>{renderZyniMarkdown(text)}</>;
}
